import React, { useEffect, useState } from "react";

type MastersTask = {
  id: string;
  title: string;
  description: string;
};

type MastersProgress = {
  name: string;
  optionalTasks: Record<string, boolean>;
};

const API_BASE =
  "https://sopronpogo-api.greenmeadow-9f8cab91.polandcentral.azurecontainerapps.io/api";

const TASKS_URL = `${API_BASE}/masters/tasks`;
const PROGRESS_URL = `${API_BASE}/masters/progress`;
const ADD_USER_URL = `${API_BASE}/masters/progress/user`;
const SAVE_MATRIX_URL = `${API_BASE}/masters/progress/matrix`;

export default function MasterChallengeMatrix() {
  const [tasks, setTasks] = useState<MastersTask[]>([]);
  const [players, setPlayers] = useState<MastersProgress[]>([]);

  const [newPlayerName, setNewPlayerName] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isAddingPlayer, setIsAddingPlayer] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(
    null
  );

  useEffect(() => {
    let alive = true;

    async function loadData() {
      setIsLoading(true);
      setError(null);

      try {
        const [tasksResponse, progressResponse] = await Promise.all([
          fetch(TASKS_URL),
          fetch(PROGRESS_URL),
        ]);

        if (!tasksResponse.ok) {
          throw new Error(
            "Nem sikerült betölteni a Masters feladatokat."
          );
        }

        if (!progressResponse.ok) {
          throw new Error(
            "Nem sikerült betölteni a Masters teljesítéseket."
          );
        }

        const tasksData =
          (await tasksResponse.json()) as MastersTask[];

        const progressData =
          (await progressResponse.json()) as MastersProgress[];

        if (!alive) {
          return;
        }

        setTasks(tasksData);
        setPlayers(progressData);
      } catch (loadError) {
        if (!alive) {
          return;
        }

        setError(
          loadError instanceof Error
            ? loadError.message
            : "Ismeretlen hiba történt."
        );
      } finally {
        if (alive) {
          setIsLoading(false);
        }
      }
    }

    void loadData();

    return () => {
      alive = false;
    };
  }, []);

  const toggleTask = (
    playerName: string,
    taskId: string
  ) => {
    setPlayers((current) =>
      current.map((player) => {
        if (player.name !== playerName) {
          return player;
        }

        return {
          ...player,
          optionalTasks: {
            ...player.optionalTasks,
            [taskId]: !player.optionalTasks?.[taskId],
          },
        };
      })
    );

    setSuccessMessage(null);
  };

  const getCompletedCount = (
    player: MastersProgress
  ): number => {
    return tasks.filter(
      (task) => player.optionalTasks?.[task.id] === true
    ).length;
  };

  const addPlayer = async () => {
    const name = newPlayerName.trim();

    if (!name) {
      setError("Add meg a játékos nevét.");
      return;
    }

    setIsAddingPlayer(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(ADD_USER_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
        }),
      });

      if (!response.ok) {
        const message = await response.text();

        throw new Error(
          message || "Nem sikerült hozzáadni a játékost."
        );
      }

      const newPlayer =
        (await response.json()) as MastersProgress;

      setPlayers((current) => [
        ...current,
        newPlayer,
      ]);

      setNewPlayerName("");

      setSuccessMessage(
        `${newPlayer.name} sikeresen hozzáadva.`
      );
    } catch (addError) {
      setError(
        addError instanceof Error
          ? addError.message
          : "Nem sikerült hozzáadni a játékost."
      );
    } finally {
      setIsAddingPlayer(false);
    }
  };

  const saveMatrix = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      const response = await fetch(SAVE_MATRIX_URL, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          users: players,
        }),
      });

      if (!response.ok) {
        const message = await response.text();

        throw new Error(
          message || "Nem sikerült menteni a Masters mátrixot."
        );
      }

      const savedPlayers =
        (await response.json()) as MastersProgress[];

      setPlayers(savedPlayers);

      setSuccessMessage(
        "Masters teljesítések sikeresen elmentve."
      );
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Nem sikerült menteni."
      );
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="surface-body">
        <h1>Masters Challenge Matrix</h1>
        <p>Betöltés...</p>
      </div>
    );
  }

  return (
    <div className="surface-body">
      <h1>Masters Challenge Matrix</h1>

      <section className="challenge-selector-section">
        <h2 className="mc-title">
          Új játékos hozzáadása
        </h2>

        <div
          style={{
            display: "flex",
            gap: "0.75rem",
            alignItems: "center",
            flexWrap: "wrap",
          }}
        >
          <input
            type="text"
            value={newPlayerName}
            placeholder="Játékos neve"
            onChange={(event) =>
              setNewPlayerName(event.target.value)
            }
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                void addPlayer();
              }
            }}
            disabled={isAddingPlayer}
          />

          <button
            type="button"
            className="challenge-matrix-save-button"
            onClick={() => void addPlayer()}
            disabled={
              isAddingPlayer ||
              newPlayerName.trim().length === 0
            }
          >
            {isAddingPlayer
              ? "Hozzáadás..."
              : "Játékos hozzáadása"}
          </button>
        </div>
      </section>

      {error && (
        <div
          className="challenge-matrix-message challenge-matrix-error"
          role="alert"
        >
          {error}
        </div>
      )}

      {successMessage && (
        <div
          className="challenge-matrix-message challenge-matrix-success"
          role="status"
        >
          {successMessage}
        </div>
      )}

      <section style={{ marginTop: "1.5rem" }}>
        <div className="challenge-matrix-heading">
          <div>
            <h2 className="mc-title">
              Játékosok és teljesítések
            </h2>

            <p>
              {players.length} játékos,{" "}
              {tasks.length} Masters feladat
            </p>
          </div>

          <button
            type="button"
            className="challenge-matrix-save-button"
            onClick={() => void saveMatrix()}
            disabled={isSaving}
          >
            {isSaving
              ? "Mentés..."
              : "Változások mentése"}
          </button>
        </div>

        <div className="table-wrap challenge-matrix-wrap">
          <table className="nice-table challenge-matrix-table">
            <thead>
              <tr>
                <th className="challenge-matrix-player-column">
                  Játékos
                </th>

                <th
                  className="challenge-matrix-challenge-column"
                  title="Teljesített feladatok"
                >
                  ✓
                </th>

                {tasks.map((task) => (
                  <th
                    key={task.id}
                    className="challenge-matrix-challenge-column"
                    title={`${task.title}: ${task.description}`}
                  >
                    <div className="challenge-column-header">
                      <span className="challenge-column-title">
                        {task.title}
                      </span>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>

            <tbody>
              {players.map((player) => {
                const completedCount =
                  getCompletedCount(player);

                const isMaster =
                  completedCount >= 10;

                return (
                  <tr key={player.name}>
                    <th
                      scope="row"
                      className="challenge-matrix-player"
                    >
                      {player.name}
                    </th>

                    <td
                      className="challenge-matrix-cell"
                      style={{
                        fontWeight: isMaster
                          ? 700
                          : undefined,
                      }}
                    >
                      {completedCount}/25
                      {isMaster ? " 🏆" : ""}
                    </td>

                    {tasks.map((task) => {
                      const inputId = [
                        "masters",
                        player.name,
                        task.id,
                      ]
                        .join("-")
                        .replace(/\s+/g, "-")
                        .toLowerCase();

                      return (
                        <td
                          key={task.id}
                          className="challenge-matrix-cell"
                        >
                          <input
                            id={inputId}
                            type="checkbox"
                            checked={
                              player.optionalTasks?.[
                                task.id
                              ] === true
                            }
                            onChange={() =>
                              toggleTask(
                                player.name,
                                task.id
                              )
                            }
                            aria-label={`${player.name}: ${task.title}`}
                          />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}